import React from 'react';
import { IoTimeOutline, IoWarningOutline, IoRefreshOutline } from 'react-icons/io5';

interface Props {
  /** Localized `translations.bot.sessionLimit` sub-tree. */
  t?: {
    title?: string;
    body?: string;
    countdown?: string;
    existingChats?: string;
    ready?: string;
    genericTitle?: string;
    genericBody?: string;
    endedTitle?: string;
    endedBody?: string;
    endedCta?: string;
  };
  /** True while the per-IP new-session quota is in effect. */
  cooldownActive: boolean;
  /** Pre-formatted `mm:ss` / `h:mm:ss` remaining. */
  cooldownLabel: string;
  /** Cooldown just elapsed — invite the user to try again. */
  cooldownJustEnded: boolean;
  /** A non-quota session-creation failure (network, 5xx, eligibility). */
  genericError: boolean;
  /** Whether the user has other chats they can still use, which changes the
   *  advice we give: "your existing chats still work" is only true if they do. */
  hasOtherChats: boolean;
  /**
   * This conversation is over — archived, or its token budget ran out. The BE
   * rejects every further message on it with 403, so the only way forward is a
   * new chat.
   */
  sessionEnded: boolean;
  /** Starts a fresh chat. Rendered as the ended state's only action. */
  onStartNewChat: () => void;
}

/**
 * Notice strip between the transcript and the composer, explaining why a new
 * chat can't be started right now.
 *
 * Deliberately NOT a fake bot message: the countdown re-renders every second
 * (which would thrash the whole message list), it must survive a reload while
 * the server-side cooldown is still running, and it isn't part of the
 * conversation the backoffice transcript view renders.
 */
const BotSessionNotice: React.FC<Props> = ({
  t,
  cooldownActive,
  cooldownLabel,
  cooldownJustEnded,
  genericError,
  hasOtherChats,
  sessionEnded,
  onStartNewChat,
}) => {
  if (cooldownActive) {
    const countdown = (t?.countdown ?? 'You can start a new chat in {{time}}').replace(
      '{{time}}',
      cooldownLabel,
    );
    return (
      <div className="bot-session-notice" role="status" aria-live="polite">
        <IoTimeOutline className="bot-session-notice__icon" size={18} />
        <div className="bot-session-notice__text">
          <div className="bot-session-notice__title">
            {t?.title ?? "You've started too many chats"}
          </div>
          <div className="bot-session-notice__body">
            {t?.body ?? 'Please wait a moment before starting a new one.'}
          </div>
          {/* aria-live on the wrapper would re-announce every tick; the countdown
              is decorative once the body text has been read out. */}
          <div className="bot-session-notice__countdown" aria-hidden="true">
            {countdown}
          </div>
          {hasOtherChats && (
            <div className="bot-session-notice__hint">
              {t?.existingChats ?? 'Your existing chats still work as usual.'}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Ranked below the cooldown on purpose: when a session ends AND the per-IP
  // new-session quota is in effect, "you can start a new chat in mm:ss" is the
  // more useful sentence — offering a button that would immediately 403 is not.
  if (sessionEnded) {
    return (
      <div className="bot-session-notice bot-session-notice--ended" role="status" aria-live="polite">
        <IoRefreshOutline className="bot-session-notice__icon" size={18} />
        <div className="bot-session-notice__text">
          <div className="bot-session-notice__title">
            {t?.endedTitle ?? 'This conversation has ended'}
          </div>
          <div className="bot-session-notice__body">
            {t?.endedBody ?? 'Start a new chat to keep planning your trip.'}
          </div>
          <button
            type="button"
            className="bot-session-notice__cta"
            onClick={onStartNewChat}
          >
            {t?.endedCta ?? 'Start a new chat'}
          </button>
        </div>
      </div>
    );
  }

  if (cooldownJustEnded) {
    return (
      <div className="bot-session-notice bot-session-notice--ready" role="status" aria-live="polite">
        <IoTimeOutline className="bot-session-notice__icon" size={18} />
        <div className="bot-session-notice__text">
          <div className="bot-session-notice__title">
            {t?.ready ?? 'You can start a new chat now.'}
          </div>
        </div>
      </div>
    );
  }

  if (genericError) {
    return (
      <div className="bot-session-notice bot-session-notice--error" role="alert">
        <IoWarningOutline className="bot-session-notice__icon" size={18} />
        <div className="bot-session-notice__text">
          <div className="bot-session-notice__title">
            {t?.genericTitle ?? "Couldn't start the chat"}
          </div>
          <div className="bot-session-notice__body">
            {t?.genericBody ?? 'Something went wrong on our side. Please try sending again.'}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BotSessionNotice;
